// Cognito Hosted UI authorization-code-with-PKCE flow.
//
// Three entry points:
//   - signIn(returnTo): start the flow; user is redirected to Cognito.
//   - completeSignIn(code, state): called from the /auth/callback page
//                                  after Cognito redirects back.
//   - signOut(): clears local tokens + sends the user to Cognito's
//                 logout endpoint so the federated session is also gone.
//
// Plus refreshIfNeeded() — used by the API client and the auth context to
// keep id_tokens fresh without forcing the user to sign back in.

import { config } from '@/config';
import {
  consumePkce,
  generatePkce,
  stashPkce,
  type ConsumedPkce,
} from './pkce';
import { clear as clearTokens, load as loadTokens, save as saveTokens } from './auth-storage';

const REFRESH_SLOP_MS = 60_000; // refresh 60s before expiry

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: 'Bearer';
}

function authorizeUrl(challenge: string, state: string): string {
  const params = new URLSearchParams({
    client_id: config.cognito.clientId,
    response_type: 'code',
    redirect_uri: config.cognito.redirectUri,
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  return `https://${config.cognito.domain}/oauth2/authorize?${params}`;
}

function logoutUrl(): string {
  const params = new URLSearchParams({
    client_id: config.cognito.clientId,
    logout_uri: `${window.location.origin}/auth/signed-out`,
  });
  return `https://${config.cognito.domain}/logout?${params}`;
}

async function exchangeCode(code: string, verifier: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.cognito.clientId,
    code,
    redirect_uri: config.cognito.redirectUri,
    code_verifier: verifier,
  });
  const response = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    throw new Error(`token_exchange_failed: ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
}

async function refreshToken(refreshTok: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.cognito.clientId,
    refresh_token: refreshTok,
  });
  const response = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    throw new Error(`refresh_failed: ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
}

// ---------------------------------------------------------------------------

/** Kick off sign-in. Returns a Promise that resolves only if the redirect
 *  fails (normally the page navigates away before the promise settles). */
export async function signIn(returnTo: string = window.location.pathname): Promise<void> {
  const pkce = await generatePkce();
  stashPkce(pkce, returnTo);
  window.location.assign(authorizeUrl(pkce.challenge, pkce.state));
}

export interface CompletedSignIn {
  returnTo: string;
}

/** Finish sign-in after Cognito redirects back with ?code=&state=. Verifies
 *  the state matches the one we stashed (CSRF defense), exchanges the code
 *  for tokens, and persists them. Returns where the user should be sent
 *  next based on what they were trying to reach pre-redirect. */
export async function completeSignIn(
  code: string,
  state: string,
): Promise<CompletedSignIn> {
  const stashed: ConsumedPkce | null = consumePkce();
  if (!stashed) throw new Error('no_pkce_state');
  if (stashed.state !== state) throw new Error('state_mismatch');
  const tokens = await exchangeCode(code, stashed.verifier);
  saveTokens({
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    // refresh_token is omitted on token-refresh responses but present on
    // initial code-exchange. Fall back to a stored copy when refreshing.
    refreshToken: tokens.refresh_token ?? '',
    expiresAt: Date.now() + tokens.expires_in * 1000,
  });
  return { returnTo: stashed.returnTo };
}

/** If the current id_token is missing, expired, or about to expire, attempt
 *  a refresh. Returns the up-to-date id_token, or null if no valid session
 *  is recoverable (caller should redirect to sign-in). */
export async function refreshIfNeeded(): Promise<string | null> {
  const stored = loadTokens();
  if (!stored) return null;
  if (stored.expiresAt - Date.now() > REFRESH_SLOP_MS) {
    return stored.idToken;
  }
  if (!stored.refreshToken) {
    clearTokens();
    return null;
  }
  try {
    const fresh = await refreshToken(stored.refreshToken);
    saveTokens({
      idToken: fresh.id_token,
      accessToken: fresh.access_token,
      refreshToken: fresh.refresh_token ?? stored.refreshToken,
      expiresAt: Date.now() + fresh.expires_in * 1000,
    });
    return fresh.id_token;
  } catch {
    clearTokens();
    return null;
  }
}

/** Sign out locally + at Cognito. Local cleanup is best-effort even if the
 *  redirect fails. */
export function signOut(): void {
  clearTokens();
  window.location.assign(logoutUrl());
}
